import * as React from "react";
import classNames from "classnames";
import { connect } from "react-redux";
import { StoreState } from "../../reducers/root-reducer";
import { WithStyles, Theme, createStyles, withStyles } from "@material-ui/core";
import { InjectedTranslateProps, translate } from "react-i18next";
import { Table } from "../../api/tables";
import { format } from "../../util/date-utility";

import { Meal } from '../../api/meals';
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import MUITable from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { Reservation } from "../../api/reservations";
import { notEmpty, convertPhoneNumberToLink, getReadablePaymentStatus } from "../../util/general-utility";
import { formatDate } from "../../util/date-utility";
import { ConnectedTiteledPaper } from "../layout/titeled-paper"; 
import { Payment } from "../../api/payments";

interface State {}

export interface ReservationInfoboxProps {}

interface ConnectedReservationInfoboxProps extends WithStyles<typeof styles> {
	activeReservationId: string;
	reservations: Reservation[];
	tables: Table[];
	infoBarVisible: Boolean;
	availableMeals: Meal[];
}

type Props = ReservationInfoboxProps &
	ConnectedReservationInfoboxProps &
	InjectedTranslateProps;

const reservationPropertyLabels: { [name: string]: any } = {
	id: "ID",
	date: "Datum",
	time: "Uhrzeit",
	peopleCount: "Personen",
	primaryName: "Name",
	phoneNumber: "Telefon",
	email: "E-Mail",
	notes: "Anmerkungen",
	payment: "Zahlung",
	meals: "Speisen"
};

interface ReservationWithSignature extends Reservation {
	[index: string]: any;
}

export class ReservationInfobox extends React.Component<Props, State> {
	public state: State = {};

	formatReservation(reservation: ReservationWithSignature) {
		const { tables } = this.props;

		const formatValue = function<T extends keyof Reservation>(
			key: T,
			value: Reservation[T]
		) {
			switch (key) {
				case "tableId":
					return tables.filter(t => t.id === value)[0].displayName;

				case "date":
					return formatDate(value as Date);

				case "time":
					return format(value.toString(), "HH:mm");

				case "notes":
					return value.toString().replace(/\n/g, "<br />");

				default:
					return value;
			}
		};

		const validTime = function<T extends keyof Reservation>(
			key: T,
			value: Reservation[T]
		) {
			if (key === "time" && value.toString().substr(0, 4) === "0001") {
				return false;
			}

			return true;
		};

		return Object.keys(reservation)
			.filter(
				(k: keyof Reservation) =>
					reservationPropertyLabels[k] !== undefined &&
					notEmpty(reservation[k]) &&
					validTime(k, reservation[k]) &&
					k !== "id"
			)
			.map((k: keyof Reservation) => {
				return {
					key: k,
					label: reservationPropertyLabels[k],
					value: formatValue(k, reservation[k])
				};
			});
	}

	renderCellValue<T extends keyof Reservation>(
		key: T,
		value: Reservation[T]
	) {
		const { classes, availableMeals } = this.props;
		
		switch (key) {
			case "email":
				return (
					<Typography>
						<a href={"mailto:" + value} title={value.toString()}>
							{value}
						</a>
					</Typography>
				);

			case "phoneNumber":
				return (
					<Typography>
						<a
							href={convertPhoneNumberToLink(value.toString())}
							title={value.toString()}
						>
							{value}
						</a>
					</Typography>
				);

			case "notes":
				return (
					<Typography
						dangerouslySetInnerHTML={{ __html: value.toString() }}
					/>
				);

			case "meals":
				if(value && Object.keys(value).length > 0) {
					return (
						<Typography>
							{Object.keys(value).map(id => (
								<div key={id}>{(value as any)[id]}x {availableMeals.find(m => m.id === id).name}</div>
							))}
						</Typography>
					);
				}
				return <Typography></Typography>;

			case "payment":				
				return (
					<Typography>
						<span className={classNames(classes.paymentState, {
							[classes.paymentNotCompleted]: (value as Payment).state !== "completed",
							[classes.paymentCharged]: (value as Payment).chargeState === "paid",
						})}>
							{getReadablePaymentStatus(value as Payment)}
						</span>
					</Typography>
				);

			default:
				return <Typography>{value}</Typography>;
		}
	}

	render() {
		const { classes, reservations, activeReservationId, infoBarVisible } = this.props;
		const reservation =
			activeReservationId !== null
				? reservations.filter(r => r.id === activeReservationId)[0]
				: null;

		return (
			<div className={classes.root}>
				<ConnectedTiteledPaper title="Reservierungsdetails">
					{reservation ? (
						<MUITable>
							<TableBody>
								{this.formatReservation(reservation).map(d => (
									<TableRow key={d.key}>
										<TableCell
											className={classes.titleCell}
										>
											<Typography>{d.label}</Typography>
										</TableCell>
										<TableCell
											className={classes.valueCell}
										>
											{this.renderCellValue(
												d.key,
												d.value
											)}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</MUITable>
					) : (
						<span> 
							{infoBarVisible ? ( 
								<Typography className={classes.note}>
									Keine Reservierung ausgewählt!
								</Typography>
							) : (
								<Typography className={classes.note}>
									Klicke rechts oben auf das (?) Symbol, um diese Funktion zu aktivieren.
								</Typography>
							)}
						</span>
					)}
				</ConnectedTiteledPaper>
			</div>
		);
	}
}

const mapStateToProps = (state: StoreState) => ({
	activeReservationId: state.overviewActiveReservationId,
	infoBarVisible: state.overviewInfoBarVisible,
	reservations: state.reservations,
	tables: state.tables,
	availableMeals: state.settings.meals
});

const styles = (theme: Theme) =>
	createStyles({
		root: {
			marginTop: 25
		},
		paper: {
			padding: 20
		},
		title: {
			marginBottom: 10
		},
		note: {
			fontStyle: "italic"
		},
		titleCell: {
			maxWidth: 120,
			width: 120,
			paddingLeft: 0,
			paddingRight: 0
		},
		valueCell: {
			"& a": {
				textDecoration: "none",
				color: theme.palette.text.primary
			}
		},
		paymentState: { color: "green" },
		paymentNotCompleted: { color: "red" },
		paymentCharged: { color: theme.status.reservation.charged }
	});

export const ConnectedReservationInfobox = translate("translations")(
	withStyles(styles)(connect(mapStateToProps)(ReservationInfobox))
);
